import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { CSVLink } from 'react-csv'
import cx from 'classnames'
import { NO_DATA_MESSAGE } from '../../../utils/constants'
import {
  formatDate,
  formatPercentOneDecimal,
  formatThousand,
  formatWithCommas,
} from '../../../utils/functions'
import AsOfDate from '../../AsOfDate'
import ScrollingTable from '../../Tables/ScrollingTable'
import { getFileName, getTotalMarketValue, getTotalNetAssets } from './index'

class DailyFullHoldingsFixedIncome extends Component {
  constructor(props) {
    super(props)

    const maturityDateRenderer = (cellProps) => (
      <span>
        {cellProps?.value && cellProps?.row?.assetGroup === 'FIXED INCOME'
          ? formatDate(cellProps?.value)
          : '--'}
      </span>
    )

    const renderThousands = (cellProps) => {
      if (!cellProps.value || isNaN(Number(cellProps.value))) {
        return <span>--</span>
      }
      return <span>{formatThousand(cellProps.value, 0)}</span>
    }

    const asofdate = get(props, 'data.fullHoldings[0].calendar.date')
    const data = get(props, 'data.fullHoldings', []) || []

    const columns = [
      {
        Header: 'Company Name',
        accessor: 'securityName',
      },
      {
        Header: 'Category Name',
        accessor: 'assetGroup',
      },
      {
        Header: 'Cusip',
        accessor: 'cusip',
      },
      {
        Header: 'Shares',
        accessor: 'shares',
        Cell: (cellProps) => <span>{formatWithCommas(cellProps.value)}</span>,
      },
      {
        Header: 'Maturity Date',
        accessor: 'maturityDate',
        Cell: maturityDateRenderer,
      },
      {
        Header: 'Coupon Rate (%)',
        accessor: 'couponRate',
        Cell: (cellProps) => {
          if (!cellProps.value || isNaN(Number(cellProps.value))) {
            return <span>--</span>
          }
          return <span>{parseFloat(cellProps.value).toFixed(1)}</span>
        },
      },
      {
        Header: "Market Value ($000's)",
        accessor: 'marketValue',
        Cell: renderThousands,
      },
      {
        Header: '% of Net Assets',
        accessor: 'weight',
        Cell: (cellProps) => (
          <span>{formatPercentOneDecimal(cellProps.value)}</span>
        ),
      },
    ]

    const fileName = getFileName(props.fundTitle, asofdate)
    const fileHeaders = [
      { label: 'NAME', key: 'securityName' },
      { label: 'CATEGORY', key: 'assetGroup' },
      { label: 'CUSIP', key: 'cusip' },
      { label: 'MATURITY_DATE', key: 'maturityDate' },
      { label: 'COUPON_RATE', key: 'couponRate' },
      { label: 'VALUE (USD)', key: 'marketValue' },
      { label: '% OF TOTAL NET ASSETS', key: 'weight' },
    ]

    const totalMarketValue = getTotalMarketValue(data)
    const totalNetAssetsWeight = getTotalNetAssets(data)
    const finalNetAssetsWeight = totalNetAssetsWeight / 100

    const footerData = [
      {
        securityName: 'Total',
        assetGroup: '',
        cusip: '',
        shares: '',
        maturityDate: '',
        couponRate: '',
        marketValue: totalMarketValue,
        weight: finalNetAssetsWeight,
      },
      {
        securityName: 'Cash and Other Assets Less Liabilities',
        assetGroup: '',
        cusip: '',
        shares: '',
        maturityDate: '',
        couponRate: '',
        marketValue: '',
        weight: 1 - finalNetAssetsWeight,
      },
      {
        securityName: 'Total Net Assets',
        assetGroup: '',
        cusip: '',
        shares: '',
        maturityDate: '',
        couponRate: '',
        marketValue: '',
        weight: 1,
      },
    ]

    this.state = {
      columns,
      data,
      fileName,
      fileHeaders,
      asofdate,
      footerData,
    }
  }

  getTableOptions() {
    const { presentation } = this.props
    return { ...presentation, stripedTable: true }
  }

  render() {
    const { presentation, fiscalQuarterEndDoc } = this.props
    const { data, columns, asofdate, footerData, fileName, fileHeaders } =
      this.state

    if (data.length === 0) {
      return <p>{NO_DATA_MESSAGE}</p>
    }

    return (
      <>
        <AsOfDate date={asofdate} />
        <div className={cx('doc-links', 'withHarborThemeWrapper')}>
          {'Download:'}
          <CSVLink filename={fileName} headers={fileHeaders} data={data}>
            &nbsp;Daily Full Holdings (csv)
          </CSVLink>
          {fiscalQuarterEndDoc && ' | '}
          {fiscalQuarterEndDoc?.document && (
            <a
              href={fiscalQuarterEndDoc.document.publicDocURL}
              target="_blank"
              rel="noreferrer"
            >
              Fiscal Quarter-End Holdings (pdf)
            </a>
          )}
        </div>
        <ScrollingTable
          columns={columns}
          data={data}
          footerData={footerData}
          options={this.getTableOptions()}
        />
      </>
    )
  }
}

DailyFullHoldingsFixedIncome.propTypes = {
  data: PropTypes.shape({
    fullHoldings: PropTypes.array,
  }),
  presentation: PropTypes.object,
  fiscalQuarterEndDoc: PropTypes.object,
  fundTitle: PropTypes.string,
}

export default DailyFullHoldingsFixedIncome